export type Endpoints = {
  authorizeSessionUrl: string;
  getManifestUrl: string;
  getAdyenSessionUrl: string;
  processPaymentUrl: string;
  authorizeApplePayUrl: string;
  getTransactionStatusUrl: string;
  getCardTokenStatusUrl: string;
  getPaymentMethods: string;
  payWithStoredPayment: string;
  getPBLDataUrl: string;
  confirmation: string;
  sendConfirmationEmail: string;
  submitAdyenTokenUrl: string;
};

export enum Environments {
  local = 'local',
  localProxy = 'localProxy',
  dev1 = 'dev1',
  dev2 = 'dev2',
  qa1 = 'qa1',
  qa2 = 'qa2',
  uat1 = 'uat1',
  uat2 = 'uat2',
  stage = 'stg',
  prod = 'prod',
}

const endpointPath = {
  authorizeSessionUrl: '/payments-payments/payments/v1/tokens/authorize-session',
  getManifestUrl: '/payments-payments/payments/v1/manifests',
  getAdyenSessionUrl: '/payments-payments/payments/v1/adyen-session',
  processPaymentUrl: '/payments-payments/payments/v1/manifests/pay',
  authorizeApplePayUrl: '/payments-payments/payments/v1/apple/authorize-session',
  getTransactionStatusUrl: '/payments-payments/payments/v1/transactions/{id}/status',
  getCardTokenStatusUrl: '/payments-payments/payments/v1/tokens/{clientToken}/status',
  getPaymentMethods: '/payments-payments/payments/v1/payment-methods',
  payWithStoredPayment: '/payments-payments/payments/v1/payment-methods/pay',
  getPBLDataUrl: '/payments-payments/payments/v1/pbl-data',
  confirmation: '/payments-payments/payments/v1/confirmation',
  sendConfirmationEmail: '/payments-payments/payments/v1/send-email',
  submitAdyenTokenUrl: '/payments-payments/payments/v1/tokens',
};

export const getEnvironmentFromHost = (hostname: string): Environments => {
  if (hostname.search('localhost') > -1) {
    return Environments.local;
  } else if (hostname.startsWith('payments.app.ace.aaa.com')) {
    return Environments.localProxy;
  } else {
    if (hostname.startsWith('appdev1') || hostname.startsWith('app.dev')) {
      return Environments.dev1;
    } else if (hostname.startsWith('appdev2')) {
      return Environments.dev2;
    } else if (hostname.startsWith('appqa1') || hostname.startsWith('app.qa')) {
      return Environments.qa1;
    } else if (hostname.startsWith('appqa2')) {
      return Environments.qa2;
    } else if (hostname.startsWith('appuat1') || hostname.startsWith('app.uat')) {
      return Environments.uat1;
    } else if (hostname.startsWith('appuat2')) {
      return Environments.uat2;
    } else if (hostname.startsWith('appstage') || hostname.startsWith('app.stage')) {
      return Environments.stage;
    } else if (hostname.startsWith('app.')) {
      return Environments.prod;
    } else {
      return Environments.dev1;
    }
  }
};

const getEnvironmentForDomain = (env: Environments): string => {
  if (env === Environments.dev1 || env === Environments.dev2) {
    return 'dev';
  } else if (env === Environments.qa1 || env === Environments.qa2) {
    return 'qa';
  } else if (env === Environments.uat1 || env === Environments.uat2) {
    return 'uat';
  } else if (env === Environments.stage) {
    return 'stg';
  } else {
    return 'dev';
  }
};

export const getCommonApiDomain = (env: Environments): string => {
  if (env === Environments.prod) {
    return `common.api.ace.aaa.com`;
  } else if (env === Environments.local) {
    return window.location.host;
  } else {
    return `common.api.${getEnvironmentForDomain(env)}.ace.aaa.com`;
  }
};

export const getAppleTokenNotificationURL = (hostname: string): string => {
  const env = getEnvironmentFromHost(hostname);
  const basePath = '/payments-payments/payments/v1/apple/token-notifications';
  return `https://${getCommonApiDomain(env)}${basePath}`;
};

export const getEndpoints = (hostname: string): Endpoints => {
  const env = getEnvironmentFromHost(hostname);
  const _endpoints: Endpoints = {
    authorizeSessionUrl: `https://${getCommonApiDomain(env)}${endpointPath.authorizeSessionUrl}`,
    getManifestUrl: `https://${getCommonApiDomain(env)}${endpointPath.getManifestUrl}`,
    getAdyenSessionUrl: `https://${getCommonApiDomain(env)}${endpointPath.getAdyenSessionUrl}`,
    processPaymentUrl: `https://${getCommonApiDomain(env)}${endpointPath.processPaymentUrl}`,
    authorizeApplePayUrl: `https://${getCommonApiDomain(env)}${endpointPath.authorizeApplePayUrl}`,
    getTransactionStatusUrl: `https://${getCommonApiDomain(env)}${endpointPath.getTransactionStatusUrl}`,
    getCardTokenStatusUrl: `https://${getCommonApiDomain(env)}${endpointPath.getCardTokenStatusUrl}`,
    getPaymentMethods: `https://${getCommonApiDomain(env)}${endpointPath.getPaymentMethods}`,
    payWithStoredPayment: `https://${getCommonApiDomain(env)}${endpointPath.payWithStoredPayment}`,
    getPBLDataUrl: `https://${getCommonApiDomain(env)}${endpointPath.getPBLDataUrl}`,
    confirmation: `https://${getCommonApiDomain(env)}${endpointPath.confirmation}`,
    sendConfirmationEmail: `https://${getCommonApiDomain(env)}${endpointPath.sendConfirmationEmail}`,
    submitAdyenTokenUrl: `https://${getCommonApiDomain(env)}${endpointPath.submitAdyenTokenUrl}`,
  };
  return _endpoints;
};

export const endpoints = getEndpoints(window.location.hostname);
